import {
  API_GET_ALL_USERS,
  API_GET_SEARCH_USERS,
  API_GET_USER_DETAILS,
  API_GET_USER_DETAILS_UUID,
  API_GET_USERS_FOR_STORE,
  API_POST_ASSIGN_USER_STORES,
  API_POST_CHANGE_USER_PWD,
  API_POST_REMOVE_USER_STORES,
  API_POST_UPSERT_USER,
} from 'common/constants/apis';
import { CRASHLYTICS_AUTH_ERROR } from 'common/constants/errors';
import { IServerResponse, ServerResponse } from 'common/interfaces';
import { IUser, IUserDetailsResp, IUserListResp, IUserSearchCriteria, IUserUpdateResp } from 'common/interfaces/iuser';
import UILogger, { LogRecordType, LogSeverityType } from 'common/utils/logger';
import httpClient from './config';
import { DEFAULT_RETRY_CONFIG } from 'common/constants';
import { AxiosRequestConfig } from 'axios';
import { IStore, IStoreUser } from 'common/interfaces/istore';

export const postUpdateUser = (payload: IUser): Promise<IUserUpdateResp> => {
  UILogger.info('Sending update user request', payload);
  return httpClient
    .post(
      API_POST_UPSERT_USER,
      payload,
      // DEFAULT_RETRY_CONFIG as AxiosRequestConfig,
    )
    .then((res) => {
      UILogger.log('add user response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error upserting user:::', error.response.data);
      return error.response.data;
    });
};

export const postDeleteUser = (userId: number): Promise<ServerResponse> => {
  UILogger.info('Sending deleter user request for user: ', userId);
  return httpClient
    .delete(
      API_POST_UPSERT_USER + userId,
      // DEFAULT_RETRY_CONFIG as AxiosRequestConfig,
    )
    .then((res) => {
      UILogger.log('delete user response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error deleting user:::', error.response.data);
      return error.response.data;
    });
};

export const getUserList = async (pageNo: number, pageSize: number): Promise<IUserListResp> => {
  UILogger.log('UserService: getting users:, pageNo:', pageNo, 'pageSize:', pageSize);
  return httpClient
    .get(API_GET_ALL_USERS + '/' + pageNo + '/' + pageSize)
    .then((res) => {
      UILogger.log('UserService: getting users: done, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as IUserListResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in getting users',
          error,
        );
      }
      UILogger.log('Error in getting users. status:', error?.response?.status);
      return { status: { success: false } } as IUserListResp;
    });
};

export const searchUser = async (
  criteria: IUserSearchCriteria,
  pageNo: number,
  pageSize: number,
): Promise<IUserListResp> => {
  UILogger.log('UserService: searching users:, pageNo:', pageNo, 'pageSize:', pageSize, 'criteria: ', criteria);
  return httpClient
    .post(API_GET_SEARCH_USERS + '/' + pageNo + '/' + pageSize, criteria)
    .then((res) => {
      UILogger.log('UserService: searching users: done, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as IUserListResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in search users',
          error,
        );
      }
      UILogger.log('Error in search users. status:', error?.response?.status);
      return { status: { success: false } } as IUserListResp;
    });
};

export const getUsersForStore = async (
  storeId: number,
  roleId: number,
  pageNo: number,
  pageSize: number,
): Promise<IUserListResp> => {
  UILogger.log(
    'UserService: getting users:,',
    'storeId:',
    storeId,
    'roleID:',
    roleId,
    'pageNo:',
    pageNo,
    'pageSize:',
    pageSize,
  );
  return httpClient
    .get(API_GET_USERS_FOR_STORE + storeId + '/' + roleId + '/' + pageNo + '/' + pageSize)
    .then((res) => {
      UILogger.log('UserService: getting users for store: done, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as IUserListResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in getting users for store',
          error,
        );
      }
      UILogger.log('Error in getting users for store. status:', error?.response?.status);
      return { status: { success: false } } as IUserListResp;
    });
};

export const getUserDetails = (userId: number): Promise<IUserDetailsResp> => {
  UILogger.info('Getting details for user: ', userId);
  return httpClient
    .get(API_GET_USER_DETAILS.replace('{id}', userId.toString()), DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then((res) => {
      UILogger.log('getUserDetails response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      UILogger.error('Error getting user details:::', error.response.data);
      return error.response.data;
    });
};
export const getUserDetailsByUUId = (uuId: string): Promise<IUserDetailsResp> => {
  UILogger.info('Getting details for uuId: ', uuId);
  return httpClient
    .get(API_GET_USER_DETAILS_UUID.replace('{uuId}', uuId), DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then((res) => {
      UILogger.log('getUserDetails response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      UILogger.error('Error getting user details:::', error.response.data);
      return error.response.data;
    });
};

export const postChangeUserPassword = (userId: number, newPassword: string): Promise<ServerResponse> => {
  UILogger.log('Sending change user password request to server. userId:', userId);

  return httpClient
    .post(API_POST_CHANGE_USER_PWD.replace('{id}', userId.toString()), {
      password: newPassword,
    })
    .then((res) => {
      UILogger.log('Change user password response: ', res.data, ',userId:', userId);
      return res.data;
    })
    .catch((error) => {
      UILogger.log(
        {
          recordType: LogRecordType.CRASHLYTICS,
          severity: LogSeverityType.ERROR,
          error,
          tag: CRASHLYTICS_AUTH_ERROR,
        },
        'Error in Change password for user ',
        userId,
        error,
      );
      return { status: { success: false } } as ServerResponse;
    });
};

export const postAssignStoresToUser = (userId: number, payload: IStoreUser): Promise<IServerResponse<IStore>> => {
  UILogger.info('postAssignStoresToUser: ', payload);
  return httpClient
    .post(
      API_POST_ASSIGN_USER_STORES.replace('{userId}', userId.toString()),
      payload,
      // DEFAULT_RETRY_CONFIG as AxiosRequestConfig,
    )
    .then((res) => {
      UILogger.log('postAssignStoresToUser response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error upserting user:::', error.response.data);
      return error.response.data;
    });
};
export const postRemoveStoresFromUser = (userId: number, storeId: number): Promise<IServerResponse<IStore>> => {
  UILogger.info('postRemoveStoresFromUser:storeId: ' + storeId + ',userId:' + userId);
  return httpClient
    .post(
      API_POST_REMOVE_USER_STORES.replace('{userId}', userId.toString()).replace('{storeId}', storeId.toString()),
      {},
      // DEFAULT_RETRY_CONFIG as AxiosRequestConfig,
    )
    .then((res) => {
      UILogger.log('postRemoveStoresFromUser response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error upserting user:::', error.response.data);
      return error.response.data;
    });
};
